import { KeyboardEvent, MouseEventHandler, useCallback, useMemo } from "react";
import ColorBox from "./ColorBox";
import { Color, Counted, Image, rleImage } from "../Lib/Image";

export type StitchOrientation =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

type RunRowProps = {
  row: number;
  runs: Counted<number>[];
  palette: Counted<Color>[];
  onClick?: MouseEventHandler<HTMLElement>;
};

function StitchRow(props: RunRowProps) {
  return (
    <tr>
      <td className="App-row">{props.row + 1}</td>
      <td>
        {props.runs.map((run, index) => (
          <ColorBox
            key={index}
            className="App-run"
            color={props.palette[run.value].value}
            tabIndex={0}
          >
            {run.count}
          </ColorBox>
        ))}
      </td>
    </tr>
  );
}

function reversed<T>(reverse: boolean, items: Array<T>): Array<T> {
  return reverse ? [...items].reverse() : items;
}

type StitchListProps = {
  image: Image;
  orientation: StitchOrientation;
};

export default function StitchList(props: StitchListProps) {
  const image = props.image;
  const runs = useMemo(() => rleImage(image), [image]);

  const reverseRows =
    props.orientation === "bottom-left" || props.orientation === "bottom-right";
  const reverseRowModulus =
    props.orientation === "top-right" || props.orientation === "bottom-right"
      ? 0
      : 1;

  const onKeyDown = useCallback((ev: KeyboardEvent<HTMLTableElement>) => {
    // Use XPath to find the following or preceding .App-run and give
    // it focus
    let axis;

    if (ev.key === "ArrowLeft") {
      axis = "preceding";
    } else if (ev.key === "ArrowRight") {
      axis = "following";
    } else {
      return;
    }

    const node = document.evaluate(
      `.//${axis}::*[contains(@class, "App-run")]`,
      ev.target as Node,
      null,
      XPathResult.ANY_UNORDERED_NODE_TYPE
    ).singleNodeValue;

    if (node) {
      (node as HTMLElement).focus();
      ev.preventDefault();
    }
  }, []);

  return (
    <table className="App-rows" onKeyDown={onKeyDown}>
      <tbody>
        {reversed(reverseRows, runs).map((runsRow, index) => (
          <StitchRow
            key={index}
            row={index}
            runs={reversed(index % 2 === reverseRowModulus, runsRow)}
            palette={image.palette}
          />
        ))}
      </tbody>
    </table>
  );
}
